import { useCallback, useEffect, useState } from 'react'
import { deviceStorage, STORAGE_KEYS } from '@/shared/lib/storage'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_DAYS = 30

function isDismissed(): boolean {
  const raw = deviceStorage.get(STORAGE_KEYS.device.installDismissed)
  if (!raw) return false
  const ts = Number(raw)
  if (Number.isNaN(ts)) return false
  return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000
}

function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as unknown as Record<string, unknown>).MSStream
  )
}

function isMobileOrTablet(): boolean {
  return (
    /Android|iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 0 &&
      window.matchMedia('(max-width: 1024px)').matches)
  )
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as Record<string, unknown>).standalone === true
  )
}

export type InstallPromptState =
  | { type: 'hidden' }
  | { type: 'native'; install: () => Promise<void>; dismiss: () => void }
  | { type: 'ios'; dismiss: () => void }

export function useInstallPrompt(): InstallPromptState {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [showIOS] = useState(() => isIOS() && !isStandalone() && !isDismissed())
  const [dismissed, setDismissed] = useState(false)

  // Listen for beforeinstallprompt (Chrome, Edge, Samsung Internet)
  useEffect(() => {
    function handler(e: Event) {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const dismiss = useCallback(() => {
    deviceStorage.set(STORAGE_KEYS.device.installDismissed, String(Date.now()))
    setDeferredPrompt(null)
    setDismissed(true)
  }, [])

  const install = useCallback(async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'dismissed') {
      dismiss()
    } else {
      setDeferredPrompt(null)
    }
  }, [deferredPrompt, dismiss])

  if (dismissed || isStandalone() || !isMobileOrTablet()) {
    return { type: 'hidden' }
  }

  if (deferredPrompt && !isDismissed()) {
    return { type: 'native', install, dismiss }
  }

  if (showIOS) {
    return { type: 'ios', dismiss }
  }

  return { type: 'hidden' }
}
